/**
 * Map Type Toggle DOM Component
 *
 * Creates and manages the Road/Satellite map-type toggle control.
 * Encapsulates all DOM manipulation, event binding, and cleanup.
 *
 * Structure:
 * - Container: Positioned bottom-right above zoom controls
 * - Buttons: Road and Satellite, with data attributes for event binding
 * - Styling: Uses BEM naming (nhs-c-native-interactive-site-map__road-satellite)
 *
 * @module nativeInteractiveSiteMap/components/mapTypeToggle.dom
 */

import MapTypes from 'enums/mapTypes';

/**
 * Factory function that creates a Road/Satellite toggle component.
 * Manages all DOM elements and interactions internally.
 *
 * @returns {Object} Component API with methods: getElement, attachEventListeners, updateControlState, remove
 */
export default function createMapTypeToggleDom(baseUrl) {
  const state = {
    root: null,
    roadButton: null,
    satelliteButton: null,
    currentMapType: MapTypes.Roadmap,
    eventListeners: new Map(), // Store handlers for cleanup
  };

  /**
    * Creates the HTML template for the control.
   */
  function createHtml() {
    return `
      <div class="nhs-c-native-interactive-site-map__map-type-toggle nhs-mq-native-interactive-site-map__map-type-toggle-medium" data-qa="native_ism_maptype_toggle">
        <button 
          type="button"
          class="nhs-c-native-interactive-site-map__map-type-toggle-btn nhs-mq-native-interactive-site-map__map-type-toggle-btn-medium"
          data-map-control="road"
          aria-pressed="false"
          aria-label="Switch to road map view"
          title="Road">
            <img src="${baseUrl}/road.png" alt="Satellite view icon" />
        </button>
        <button 
          type="button"
          class="nhs-c-native-interactive-site-map__map-type-toggle-btn nhs-mq-native-interactive-site-map__map-type-toggle-btn-medium"
          data-map-control="satellite"
          aria-pressed="true"
          aria-label="Switch to satellite map view"
          title="Satellite">
              <img src="${baseUrl}/satellite.png" alt="Satellite view icon" />
        </button>
      </div>
    `;
  }

  /**
    * Initializes DOM elements from the HTML template.
   */
  function initializeDom() {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = createHtml();
    state.root = wrapper.firstElementChild;
    state.roadButton = state.root?.querySelector('[data-map-control="road"]');
    state.satelliteButton = state.root?.querySelector('[data-map-control="satellite"]');
  }

  /**
    * Attaches click event listeners to toggle buttons.
   * @param {Object} handlers - { onRoadClick, onSatelliteClick }
   */
  function attachEventListeners(handlers) {
    if (state.roadButton && handlers.onRoadClick) {
      state.roadButton.addEventListener('click', handlers.onRoadClick);
      state.eventListeners.set('roadClick', { element: state.roadButton, handler: handlers.onRoadClick });
    }

    if (state.satelliteButton && handlers.onSatelliteClick) {
      state.satelliteButton.addEventListener('click', handlers.onSatelliteClick);
      state.eventListeners.set('satelliteClick', { 
        element: state.satelliteButton, handler: handlers.onSatelliteClick 
      });
    }
  }

  /**
   * Updates visual state of toggle buttons based on active map type.
    * Applies the --visible class to the non-active option (the one users can switch to).
   * @param {MapTypes} mapType - The active map type
   */
  function updateControlState(mapType) {
    const isRoad = mapType === MapTypes.Roadmap;
    const activeClass = 'nhs-c-native-interactive-site-map__map-type-toggle--visible';
    
    if (state.roadButton) {
      state.roadButton.classList.toggle(activeClass, !isRoad);
      state.roadButton.setAttribute('aria-pressed', isRoad ? 'true' : 'false');
    }
    if (state.satelliteButton) {
      state.satelliteButton.classList.toggle(activeClass, isRoad);
      state.satelliteButton.setAttribute('aria-pressed', isRoad ? 'false' : 'true');
    }
    
    state.currentMapType = mapType;
  }

  /**
    * Returns the root DOM element for insertion into the page.
   */
  function getElement() {
    return state.root;
  }

  /**
    * Removes all event listeners and cleans up the DOM.
   */
  function remove() {
    state.eventListeners.forEach(({ element, handler }) => {
      element?.removeEventListener?.('click', handler);
    });
    state.eventListeners.clear();
    state.root?.remove?.();
    state.root = null;
    state.roadButton = null;
    state.satelliteButton = null;
  }

  // Initialize DOM on creation
  initializeDom();

  // Public API
  return {
    getElement,
    attachEventListeners,
    updateControlState,
    remove,
  };
}
